import React, { useState } from 'react';
import { AnalysisResult } from '../services/projectService';
import { FaSync, FaHistory, FaPlus, FaCalculator, FaQuestionCircle, FaChevronDown, FaGavel, FaExclamationTriangle } from 'react-icons/fa';
import './AnalysisResultCard.css';
import './Tooltip.css';

/** Wraps a button so the tooltip still shows when the button is disabled */
const ButtonWithTooltip: React.FC<{
  tooltip: string;
  children: React.ReactNode;
}> = ({ tooltip, children }) => (
  <span className="button-tooltip-wrapper">
    {children}
    <span className="button-tooltip-trigger">
      <FaQuestionCircle className="tooltip-icon" />
      <span className="tooltip-text">{tooltip}</span>
    </span>
  </span>
);

interface AnalysisResultCardProps {
  result: AnalysisResult;
  isUpdating: boolean;
  currentUpdatingId: number | null;
  onReanalyze: (resultId: number) => void;
  onAssessRisk: (resultId: number) => void;
  onHistoryClick: (resultId: number) => void;
  onOpenContext: (result: AnalysisResult) => void;
  onValidateApplicability: (resultId: number, applies: boolean) => Promise<void>;
}

const stageConfig: Record<string, { label: string; color: string; glow: string }> = {
  'pending_ai_analysis':          { label: 'AI Analysis',        color: '#3b82f6', glow: 'rgba(59,130,246,0.15)' },
  'pending_applicability_review': { label: 'Needs Review',       color: '#f59e0b', glow: 'rgba(245,158,11,0.15)' },
  'pending_risk_assessment':      { label: 'Risk Assessment',    color: '#f97316', glow: 'rgba(249,115,22,0.15)' },
  'completed':                    { label: 'Completed',          color: '#10b981', glow: 'rgba(16,185,129,0.15)' },
};

const riskLevelConfig: Record<string, { bg: string; border: string; text: string }> = {
  bajo:     { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
  moderado: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  alto:     { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
  extremo:  { bg: '#fce7f3', border: '#f9a8d4', text: '#831843' },
};

const AnalysisResultCard: React.FC<AnalysisResultCardProps> = ({
  result,
  isUpdating,
  currentUpdatingId,
  onReanalyze,
  onAssessRisk,
  onHistoryClick,
  onOpenContext,
  onValidateApplicability,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isBusy = isUpdating && currentUpdatingId === result.id;
  const stage = stageConfig[result.workflow_stage] || stageConfig['pending_ai_analysis'];
  const aiApplies = result.result?.applies;
  const confidence = result.result?.confidence_score;
  const hasOverride = result.validated_applies !== null && result.result && result.validated_applies !== result.result.applies;
  // The effective (final) applies value: user decision takes priority over AI
  const effectiveApplies = result.validated_applies !== null ? result.validated_applies : aiApplies;
  const riskLevel = result.inherent_risk_level?.toLowerCase();
  const riskStyle = riskLevel ? riskLevelConfig[riskLevel] : null;

  return (
    <div
      className={`arc ${isExpanded ? 'arc--expanded' : ''}`}
      style={{ '--stage-color': stage.color, '--stage-glow': stage.glow } as React.CSSProperties}
    >
      {/* ---- Top accent bar ---- */}
      <div className="arc__accent" />

      {/* ---- Header: metadata + status ---- */}
      <div className="arc__header">
        <div className="arc__meta">
          <span className="arc__law">{result.matrix_item.ley}</span>
          <span className="arc__article">{result.matrix_item.articulo}</span>
        </div>
        <span className="arc__stage-badge" style={{ background: stage.glow, color: stage.color, borderColor: stage.color }}>
          {stage.label}
        </span>
      </div>

      {/* ---- Category row ---- */}
      <div className="arc__category-row">
        <span className="arc__category">{result.matrix_item.categoria}</span>
        <span className="arc__grupo">{result.matrix_item.grupo_delitos}</span>
      </div>

      {/* ---- KPI strip ---- */}
      {result.result && (
        <div className="arc__kpi-strip">
          <div className={`arc__kpi-applies ${hasOverride ? 'arc__kpi-applies--overridden' : ''}`}>
            {!hasOverride && <span className="arc__kpi-label">Applies</span>}
            {/* AI layer (base) */}
            <div className={`arc__applies-layer arc__applies-layer--ai ${aiApplies ? 'arc__applies--yes' : 'arc__applies--no'} ${hasOverride ? 'arc__applies-layer--faded' : ''}`}>
              {hasOverride && <span className="arc__applies-source">AI</span>}
              <span className="arc__applies-value">{aiApplies ? 'Yes' : 'No'}</span>
            </div>
            {/* User override layer (on top) */}
            {hasOverride && (
              <div className={`arc__applies-layer arc__applies-layer--user ${effectiveApplies ? 'arc__applies--yes' : 'arc__applies--no'}`}>
                <span className="arc__applies-source">Final</span>
                <span className="arc__applies-value">{effectiveApplies ? 'Yes' : 'No'}</span>
              </div>
            )}
          </div>
          {confidence != null && (
            <div className="arc__kpi">
              <span className="arc__kpi-label">Confidence</span>
              <span className="arc__kpi-value">{(confidence * 100).toFixed(0)}%</span>
            </div>
          )}
          {result.inherent_risk_score != null && riskStyle && (
            <div className="arc__kpi arc__kpi--risk" style={{ background: riskStyle.bg, borderColor: riskStyle.border }}>
              <span className="arc__kpi-label" style={{ color: riskStyle.text }}>
                Risk{!result.risk_assessed_by && <span className="arc__ai-tag">IA</span>}
              </span>
              <span className="arc__kpi-value arc__kpi-risk-score" style={{ color: riskStyle.text }}>
                {result.inherent_risk_score}
                <small className="arc__kpi-risk-level">{result.inherent_risk_level}</small>
              </span>
            </div>
          )}
          {result.probability_score != null && result.impact_score != null && (
            <div className="arc__kpi">
              <span className="arc__kpi-label">
                P x I{!result.risk_assessed_by && <span className="arc__ai-tag">IA</span>}
              </span>
              <span className="arc__kpi-value">{result.probability_score} x {result.impact_score}</span>
            </div>
          )}
        </div>
      )}

      {/* ---- Override warning ---- */}
      {hasOverride && (
        <div className="arc__override">
          <FaExclamationTriangle />
          <span>
            User override: AI suggested '{result.result!.applies ? 'Applies' : 'Does Not Apply'}',
            decision was '{result.validated_applies ? 'Applies' : 'Does Not Apply'}'
          </span>
        </div>
      )}

      {/* ---- Validation prompt (pending_applicability_review) ---- */}
      {result.workflow_stage === 'pending_applicability_review' && result.result && (
        <div className="arc__validation">
          <div className="arc__validation-header">
            <FaGavel />
            <span>Validate AI suggestion</span>
          </div>
          <div className="arc__validation-actions">
            <button
              className="arc__val-btn arc__val-btn--confirm"
              onClick={() => onValidateApplicability(result.id, true)}
              disabled={isBusy}
            >
              Applies
            </button>
            <button
              className="arc__val-btn arc__val-btn--deny"
              onClick={() => onValidateApplicability(result.id, false)}
              disabled={isBusy}
            >
              Does Not Apply
            </button>
          </div>
        </div>
      )}

      {/* ---- Does not apply badge (completed + not applicable) ---- */}
      {result.workflow_stage === 'completed' && result.validated_applies === false && (
        <div className="arc__not-applicable">
          Does Not Apply
        </div>
      )}

      {/* ---- Expandable details ---- */}
      {result.result && (
        <>
          <button className="arc__expand-toggle" onClick={() => setIsExpanded(!isExpanded)}>
            <span>{isExpanded ? 'Hide details' : 'Show details'}</span>
            <FaChevronDown className={`arc__expand-icon ${isExpanded ? 'arc__expand-icon--open' : ''}`} />
          </button>
          {isExpanded && (
            <div className="arc__details">
              {result.result.justification && (
                <div className="arc__detail-section">
                  <h4 className="arc__detail-title">Justification</h4>
                  <p className="arc__detail-text">{result.result.justification}</p>
                </div>
              )}
              {result.result.responsible && (
                <div className="arc__detail-section">
                  <h4 className="arc__detail-title">Responsible</h4>
                  <p className="arc__detail-text">{result.result.responsible}</p>
                </div>
              )}
              {result.result.processes && (
                <div className="arc__detail-section">
                  <h4 className="arc__detail-title">Processes</h4>
                  <p className="arc__detail-text">{result.result.processes}</p>
                </div>
              )}
              {result.result.controls && (
                <div className="arc__detail-section">
                  <h4 className="arc__detail-title">Controls</h4>
                  <p className="arc__detail-text">{result.result.controls}</p>
                </div>
              )}
              {result.ai_risk_justification && (
                <div className="arc__detail-section">
                  <h4 className="arc__detail-title">AI Risk Justification</h4>
                  <p className="arc__detail-text">{result.ai_risk_justification}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ---- Action buttons footer ---- */}
      <div className="arc__actions">
        <ButtonWithTooltip tooltip="Re-run AI analysis. Creates a new version you can compare in history.">
          <button className="arc__action-btn" onClick={() => onReanalyze(result.id)} disabled={isBusy}>
            <FaSync /> Re-analyze
          </button>
        </ButtonWithTooltip>
        {result.workflow_stage === 'pending_risk_assessment' && (
          <ButtonWithTooltip tooltip="Manually set probability and impact scores (1-5).">
            <button className="arc__action-btn arc__action-btn--warn" onClick={() => onAssessRisk(result.id)} disabled={isBusy}>
              <FaCalculator /> Assess Risk
            </button>
          </ButtonWithTooltip>
        )}
        <ButtonWithTooltip tooltip={result.previous_version_id ? "View previous versions and compare changes." : "No history yet. Re-analyze to create versions."}>
          <button className="arc__action-btn" onClick={() => onHistoryClick(result.id)} disabled={!result.previous_version_id || isBusy}>
            <FaHistory /> History
          </button>
        </ButtonWithTooltip>
        <ButtonWithTooltip tooltip="Add extra context to improve AI accuracy for this item.">
          <button
            className={`arc__action-btn ${result.additional_context ? 'arc__action-btn--highlight' : ''}`}
            onClick={() => onOpenContext(result)}
            disabled={isBusy}
          >
            <FaPlus /> Context
          </button>
        </ButtonWithTooltip>
      </div>
    </div>
  );
};

export default AnalysisResultCard;
